import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePublicMembershipEnabled } from "./PreLaunchGuard";
import { Link } from "react-router-dom";
import { Ship, Building, UtensilsCrossed, MapPin, Instagram, Facebook, Mail, ArrowRight, Package, Lightbulb } from "lucide-react";
import { PUBLIC_EMAILS } from "../config/publicEmails";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { getSiteSections } from "../lib/api";
import { openCookiePreferences } from "../lib/consent";
import { toast } from "sonner";

export default function Footer() {
  const { t } = useTranslation();
  const publicMembership = usePublicMembershipEnabled();
  const [sections, setSections] = useState([]);
  const [email, setEmail] = useState("");
  const [nlBusy, setNlBusy] = useState(false);
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    loadSections();
  }, []);

  const loadSections = async () => {
    try {
      const res = await getSiteSections();
      const sectionsData = Array.isArray(res) ? res : (res.data || []);
      setSections(sectionsData);
    } catch (error) {
      console.error("Error loading sections:", error);
    }
  };

  // Check if a section should be shown in footer
  const shouldShowInFooter = (slug) => {
    const section = sections.find(s => s.slug === slug);
    if (section && section.show_in_footer === false) return false;
    return true;
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const clean = (email || "").trim();
    if (!clean) return;
    setNlBusy(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: clean, source: "footer" }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        if (data.status === "already_confirmed") {
          toast.success("Vous êtes déjà inscrit·e à la newsletter.");
        } else {
          toast.success("Vérifiez votre boîte mail pour confirmer votre inscription.");
        }
        setEmail("");
      } else if (res.status === 429) {
        toast.error("Trop de tentatives. Merci de patienter quelques minutes.");
      } else {
        toast.error(typeof data.detail === "string" ? data.detail : "Une erreur est survenue.");
      }
    } catch (err) {
      toast.error("Impossible de contacter le serveur.");
    } finally {
      setNlBusy(false);
    }
  };

  // Dynamic categories based on visibility settings
  const categories = [
    { name: "Croisières", slug: "croisieres", icon: Ship },
    { name: "Hôtels", slug: "hotels", icon: Building },
    { name: "Restaurants", slug: "restaurants", icon: UtensilsCrossed },
    { name: "Destinations", slug: "destinations", icon: MapPin },
  ].filter(cat => shouldShowInFooter(cat.slug));

  return (
    <footer className="bg-primary text-primary-foreground" data-testid="footer">
      {/* Newsletter Section - More Premium */}
      <div className="border-b border-primary-foreground/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 md:py-20">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="text-center lg:text-left max-w-md">
              <h3 className="font-heading text-2xl md:text-3xl font-bold mb-3">
                {t("footer.stayInformed")}
              </h3>
              <p className="text-primary-foreground/60 font-body text-sm md:text-base">
                {t("footer.newsletterDesc")}
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-3 w-full lg:w-auto max-w-md">
              <Input 
                type="email" 
                placeholder={t("footerExt.emailPlaceholder")} 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40 w-full lg:w-72 h-12 rounded-full px-6 font-body focus:border-accent focus:ring-accent"
                required
              />
              <Button 
                type="submit"
                disabled={nlBusy}
                data-testid="footer-newsletter-submit"
                className="bg-accent hover:bg-accent/90 shrink-0 h-12 px-8 rounded-full font-semibold"
              >
                {nlBusy ? "…" : t("footer.subscribe")}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content - 4 Columns */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <div className="bg-white rounded-lg p-3 inline-block shadow-sm">
                <img 
                  src="/images/logo-itercatus.png" 
                  alt="ITERCATUS" 
                  className="h-12 w-auto max-w-[150px] object-contain"
                  loading="eager"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = '<span class="font-heading text-xl font-bold text-primary">ITERCATUS</span>';
                  }}
                />
              </div>
            </Link>
            <p className="text-accent font-body text-sm mb-1">
              {t("nav.brandTagline")}
            </p>
            <p className="text-primary-foreground/40 text-xs italic mb-4">
              {t("common.latin")}
            </p>
            <p className="text-primary-foreground/60 leading-relaxed mb-8 font-body text-sm">
              {t("footerExt.brandDesc")}
            </p>
            <div className="flex items-center gap-2 mb-6">
              <span className="text-accent font-heading text-3xl font-bold">39€</span>
              <span className="text-primary-foreground/50 font-body text-sm">{t("footerExt.perYear")}</span>
            </div>
            <div className="flex gap-3">
              <a 
                href="https://instagram.com/itercatus" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-primary-foreground/60 hover:bg-accent hover:text-white transition-all duration-300" 
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://facebook.com/itercatus" 
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-primary-foreground/60 hover:bg-accent hover:text-white transition-all duration-300" 
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Univers Voyage Column + Tests & Avis */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-primary-foreground">{t("footer.universe")}</h4>
            <ul className="space-y-4">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link
                    to={`/categorie/${cat.slug}`}
                    className="flex items-center gap-3 text-primary-foreground/60 hover:text-accent transition-colors font-body text-sm group"
                  >
                    <cat.icon className="h-4 w-4 group-hover:text-accent transition-colors" />
                    {cat.name}
                  </Link>
                </li>
              ))}
              {/* Tests & Avis - Section éditoriale */}
              {shouldShowInFooter("tests-produits") && (
                <li className="pt-2 border-t border-primary-foreground/10 mt-4">
                  <Link
                    to="/tests-produits"
                    className="flex items-center gap-3 text-primary-foreground/60 hover:text-accent transition-colors font-body text-sm group"
                  >
                    <Package className="h-4 w-4 group-hover:text-accent transition-colors" />
                    {t("footerExt.testsProducts")}
                  </Link>
                </li>
              )}
              <li>
                <Link
                  to="/categorie/conseils-voyage"
                  className="flex items-center gap-3 text-primary-foreground/60 hover:text-accent transition-colors font-body text-sm group"
                >
                  <Lightbulb className="h-4 w-4 group-hover:text-accent transition-colors" />
                  {t("footerExt.tipsTravel")}
                </Link>
              </li>
            </ul>
          </div>

          {/* Association Column - Institutionnel uniquement */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-primary-foreground">{t("footer.editorial")}</h4>
            <ul className="space-y-4">
              <li>
                <Link to="/charte-editoriale" className="text-primary-foreground/60 hover:text-accent transition-colors font-body text-sm" data-testid="footer-charte-link">
                  {t("footerExt.charter")}
                </Link>
              </li>
              <li>
                <Link to="/la-redaction" className="text-primary-foreground/60 hover:text-accent transition-colors font-body text-sm" data-testid="footer-redaction-link">
                  {t("footerExt.editorialTeam")}
                </Link>
              </li>
              <li>
                <Link to="/association" className="text-primary-foreground/60 hover:text-accent transition-colors font-body text-sm">
                  {t("footerExt.aboutUs")}
                </Link>
              </li>
              {publicMembership && (
                <li>
                  <Link to="/adherer" className="text-primary-foreground/60 hover:text-accent transition-colors font-body text-sm">
                    {t("footerExt.becomeMember")}
                  </Link>
                </li>
              )}
              {shouldShowInFooter("bons-plans") && (
                <li>
                  <Link to="/bons-plans" className="text-primary-foreground/60 hover:text-accent transition-colors font-body text-sm">
                    {t("footerExt.ourBenefits")}
                  </Link>
                </li>
              )}
              <li>
                <Link to="/recherche" className="text-primary-foreground/60 hover:text-accent transition-colors font-body text-sm">
                  {t("footerExt.blog")}
                </Link>
              </li>
              <li>
                <Link to="/soutenir" className="text-accent hover:text-accent/80 transition-colors font-body text-sm font-medium" data-testid="footer-donations-link">
                  {t("footerExt.support")}
                </Link>
              </li>
              {publicMembership && (
                <li className="pt-3 border-t border-primary-foreground/10 mt-3">
                  <Link to="/entreprises" className="text-accent hover:text-accent/80 transition-colors font-body text-sm font-medium">
                    {t("footerExt.becomePartner")}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-6 text-primary-foreground">{t("footer.contact")}</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`mailto:${PUBLIC_EMAILS.info}`} 
                  className="flex items-center gap-3 text-primary-foreground/60 hover:text-accent transition-colors font-body text-sm"
                >
                  <Mail className="h-4 w-4" />
                  {PUBLIC_EMAILS.info}
                </a>
              </li>
              <li className="pt-4">
                <p className="text-primary-foreground/40 text-xs font-body leading-relaxed uppercase tracking-wide mb-2">
                  {t("footerExt.address")}
                </p>
                <p className="text-primary-foreground/60 text-sm font-body leading-relaxed">
                  ITERCATUS<br />
                  Quartier les 3 Fonds<br />
                  13124 Peypin, France
                </p>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Minimalist */}
      <div className="border-t border-primary-foreground/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-primary-foreground/40 font-body">
              © {currentYear} ITERCATUS. {t("footer.rights")}
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-xs">
              <Link to="/mentions-legales" className="text-primary-foreground/40 hover:text-accent transition-colors font-body">
                {t("footer.legal")}
              </Link>
              <Link to="/confidentialite" className="text-primary-foreground/40 hover:text-accent transition-colors font-body">
                {t("footer.privacy")}
              </Link>
              <button
                type="button"
                onClick={openCookiePreferences}
                className="text-primary-foreground/40 hover:text-accent transition-colors font-body bg-transparent border-0 p-0 cursor-pointer"
                data-testid="footer-cookie-preferences"
              >
                {t("footer.cookies")}
              </button>
            </div>
          </div>
          <p className="mt-4 text-[11px] leading-relaxed text-primary-foreground/50 font-body text-center md:text-left" data-testid="footer-nonprofit-mention">
            {t("footerExt.nonprofit")}
            <br className="hidden md:inline" />
            <span className="whitespace-nowrap">itercatus.com</span>,{" "}
            <span className="whitespace-nowrap">itercatus.fr</span> &{" "}
            <span className="whitespace-nowrap">itercatus.eu</span> {t("footerExt.officialDomains")}
          </p>
        </div>
      </div>
    </footer>
  );
}
